import {
  Box,
  MenuItem,
  Checkbox,
  ListItemText,
  Skeleton,
  TextField,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sortBy } from 'lodash-es';
import { useLpkRunContext } from './lpk-run-context';
import { CollapsibleSection } from '../../../../components/side-panel/CollapsibleSection';
import * as api from '../../../../lib/api/lpk';
import { useFields } from '../../../../hooks/use-fields';
import { NumericInput } from './form-controls';
import { useGrowers } from '../../../../hooks/use-growers';
import { useAlerts } from '../../../../hooks/use-alerts';
import { Label } from '../../../run-rx/Label';
import { SimpleTable } from '../../../../components/table/SimpleTable';
import { CropSelector } from './CropSelector';
import { useHasChanged } from '../../../../hooks';

const aaidKey = (aaid) => `yields.${aaid}`;

export function YieldManualEntry() {
  const theme = useTheme();

  const { showError } = useAlerts();
  const { selectedId: growerId } = useGrowers();
  const { selectedAaids: aaids, selectedFields } = useFields();

  const {
    yieldQuickFill,
    yieldCropGoals,
    yieldLoaded,
    setYieldLoaded,
    getParam,
    updateParam,
    getError,
  } = useLpkRunContext();

  const quickFillChanged = useHasChanged(yieldQuickFill);
  const cropGoalsChanged = useHasChanged(yieldCropGoals);

  const [availableYield, setAvailableYield] = useState();
  const [userSelected, setUserSelected] = useState({});
  const [manualEntryOpen, setManualEntryOpen] = useState(false);

  const fields = useMemo(
    () => selectedFields.map(mapSelectedField.bind(null, availableYield)),
    [availableYield, selectedFields],
  );

  useEffect(() => {
    if (growerId && aaids.length) {
      api
        .getAvailableYield(growerId, aaids)
        .then((yields) => {
          setYieldLoaded(true);
          setAvailableYield(yields);
        })
        .catch(showError);
    }
  }, [aaids, growerId, setYieldLoaded, showError, yieldLoaded]);

  useEffect(() => {
    if (yieldQuickFill === 'last2yield' && quickFillChanged) {
      setUserSelected({});
      updateParam(
        'yields',
        Object.fromEntries(
          fields.map((field) => [
            field.aaid,
            field.yields.slice(-2).map((y) => y.id),
          ]),
        ),
      );
    }
  }, [fields, updateParam, yieldQuickFill, quickFillChanged]);

  const clearYields = useCallback(() => {
    updateParam('yields', Object.fromEntries(aaids.map((aaid) => [aaid, []])));
  }, [aaids, updateParam]);

  useEffect(() => {
    if (yieldQuickFill === 'cropGoals' && quickFillChanged) clearYields();
  }, [yieldQuickFill, quickFillChanged, clearYields]);

  useEffect(() => {
    if (yieldQuickFill === 'cropGoals' && cropGoalsChanged) {
      clearYields();
      setUserSelected(
        Object.fromEntries(
          fields.map(({ aaid }) => [
            aaid,
            [null, { ...yieldCropGoals[0] }, { ...yieldCropGoals[1] }],
          ]),
        ),
      );
    }
  }, [
    yieldCropGoals,
    fields,
    updateParam,
    yieldQuickFill,
    cropGoalsChanged,
    clearYields,
  ]);

  useEffect(() => {
    Object.entries(userSelected).forEach(([aaid, selections]) => {
      const userYields = (selections || [])
        .map((selection) => {
          const { crop, value } = selection || {};
          return crop && value ? yieldKey(selection) : null;
        })
        .filter(Boolean);
      const allSelected = (getParam(aaidKey(aaid)) || []).sort();
      const newSelected = allSelected
        .filter((y) => !y.startsWith('user'))
        .concat(userYields)
        .sort();
      updateParam(aaidKey(aaid), newSelected);
    });
  }, [getParam, updateParam, userSelected]);

  useEffect(() => {
    if (aaids.some((aaid) => getError(`yields.${aaid}`)))
      setManualEntryOpen(true);
  }, [aaids, getError]);

  return (
    <CollapsibleSection
      title="Manual Yield Entry"
      expanded={manualEntryOpen}
      onChange={(_, expanded) => setManualEntryOpen(expanded)}
      sx={{
        '& .MuiAccordionDetails-root': { px: 0 },
      }}
    >
      <SimpleTable
        sx={{
          '& table': {
            overflow: 'hidden',
            tableLayout: 'fixed',
            width: { xs: 700, lg: '100%' },
            minWidth: 'fit-content',
          },
          '& tr > *:nth-of-type(1)': { px: 1, pl: 2, width: 'auto' },
          '& tr > *:nth-of-type(2)': { width: 232, px: 1 },
          '& tr > *:nth-of-type(3)': { width: 158, px: 1 },
          '& tr > *:nth-of-type(4)': { width: 167, px: 1, pr: 2 },
          '& tr:last-of-type td': { border: 'none' },
          '& .MuiTableCell-root': { verticalAlign: 'top' },
        }}
        rows={fields.map((field) => ({
          Field: (
            <Label
              name={`lpk.yields.${field.aaid}`}
              required
              sx={{
                ...theme.typography.body2,
                '&, & p': {
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {field.name}
            </Label>
          ),
          Yield: buildHistoricalYieldSelector(field),
          'User Yield 1': buildUserYield(field, 1),
          'User Yield 2': buildUserYield(field, 2),
        }))}
      />
    </CollapsibleSection>
  );

  function buildHistoricalYieldSelector(field) {
    if (!yieldLoaded)
      return <Skeleton variant="text" width={150} height={40} />;

    const key = aaidKey(field.aaid);
    const allSelected = getParam(key) || [];
    const historicalSelected = allSelected.filter((y) => y.startsWith('hist'));

    return (
      <TextField
        size="small"
        sx={{ width: '100%' }}
        label="Hist. Yield"
        InputLabelProps={{ shrink: true }}
        select
        value={historicalSelected}
        onChange={(e) => updateParam(key, e.target.value)}
        SelectProps={{
          multiple: true,
          renderValue: (selected) =>
            selected.map((s) => s.split('-').slice(1).join(' ')).join(', '),
        }}
      >
        {field.yields.map((y, i) => (
          <MenuItem
            disabled={allSelected.length > 1 && !allSelected.includes(y.id)}
            key={i}
            value={y.id}
            sx={{ pl: 1 }}
          >
            <Checkbox checked={getParam(aaidKey(field.aaid))?.includes(y.id)} />
            <ListItemText primary={y.name} />
          </MenuItem>
        ))}
      </TextField>
    );
  }

  function buildUserYield(field, index) {
    const key = aaidKey(field.aaid);
    const all = getParam(key) || [];
    const count = (filter) => all.filter((y) => y.startsWith(filter)).length;

    function isUserYieldEntered(i) {
      const userYield = userSelected[field.aaid]?.[i] || {};
      return userYield.crop && userYield.value;
    }

    function isDisabled() {
      if (count('hist') === 2) return true;
      if (count('hist') === 0) return false;
      if (!isUserYieldEntered(1) && !isUserYieldEntered(2)) return index === 2;
      return !isUserYieldEntered(index);
    }

    function updateUserSelected(values) {
      setUserSelected((current) => {
        const userEntries = current[field.aaid] || [];
        const userEntry = userEntries[index] || {};
        userEntries[index] = Object.assign(userEntry, values);
        return { ...current, [field.aaid]: userEntries };
      });
    }

    return (
      <Box
        sx={{
          visibility: isDisabled() ? 'hidden' : 'visible',
          height: isDisabled() ? 0 : 'auto',
        }}
      >
        <CropSelector
          disabled={isDisabled()}
          sx={{ width: '100%' }}
          label={`Crop ${index}`}
          value={userSelected[field.aaid]?.[index]?.crop || ''}
          onChange={(e) => updateUserSelected({ crop: e.target.value })}
        />
        <NumericInput
          disabled={isDisabled()}
          sx={{ width: '100%', mt: -1 }}
          min={0}
          ignoreNonNumericInput
          label={`Avg. Yield ${index} (bu/acre)`}
          value={userSelected[field.aaid]?.[index]?.value || ''}
          onChange={(e) => updateUserSelected({ value: e.target.value })}
        />
      </Box>
    );
  }
}

function mapSelectedField(availableYield, field) {
  return {
    aaid: field.aaid,
    name: field.fieldName,
    yields: sortBy((availableYield?.[field.aaid] || []).map(mapYield), 'year'),
  };
}

function yieldKey({ crop, year, value }) {
  const type = year ? 'hist' : 'user';
  return `${type}-${crop}-${year || value}`;
}

function mapYield(y) {
  return { ...y, id: yieldKey(y), name: `${y.year} ${y.crop}` };
}
